'use client';

import React, { useCallback, useState } from 'react';
import { Dialog, Button, Input, Option, Select } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useFilterDrawer } from 'src/hooks/drawers/useFilterDrawer';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useCreateFilterSetMutation } from 'src/api/redux/filterSets';
import { useNotification } from 'src/context/notification';
import { ParentFilterType, ParentFilterGroup } from 'src/context/reducers/filterSetReducer';

interface SaveFilterModalProps {
  open: boolean;
  onClose: () => void;
  location: string;
}

const SaveFilterModal = ({ open, onClose, location }: SaveFilterModalProps) => {
  const { setNotification } = useNotification();
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState<string | null>('personal');
  const [createFilterSet, { isLoading }] = useCreateFilterSetMutation();

  const { getFiltersByParentType, setTempSelectedFilterPreset } =
    useFilterSets(location);
  const { saveFilterType } = useFilterDrawer();

  const getCurrentFilterSet = useCallback(() => {
    if (!saveFilterType) return null;
    // Cast saveFilterType to any since it may include values not in the ParentFilterType type
    return getFiltersByParentType(saveFilterType as ParentFilterType);
  }, [getFiltersByParentType, saveFilterType]);

  const handleClose = () => {
    setFilterName('');
    setFilterType('personal');
    onClose();
  };

  const onSubmit = async () => {
    if (!filterName?.length) return;

    try {
      // Get the current filter set
      const filterSetData = getCurrentFilterSet();
      if (!filterSetData) return;

      // Create a copy without detailed_filters
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { detailed_filters, ...filterSetWithoutDetails } = filterSetData;

      const newFilterSet = {
        ...filterSetWithoutDetails,
        label: filterName,
        type: saveFilterType,
        scope: filterType === 'company' ? 'company' : 'personal',
      };

      // Use RTK Query mutation - cache invalidation happens automatically via invalidatesTags
      const createdFilterSet = await createFilterSet({ data: newFilterSet }).unwrap();

      // Set the newly created filter set as the selected preset
      setTempSelectedFilterPreset(createdFilterSet as ParentFilterGroup);

      setNotification({
        message: 'Code set created successfully',
        type: 'success',
        title: 'Code set created',
      });

      handleClose();
    } catch (err: any) {
      setNotification({
        message: err?.data?.error_message || err?.error_message || 'Failed to create filter set',
        type: 'error',
        title: 'Filter set creation failed',
      });
    }
  };

  if (!open) return null;
  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      hideBackdrop
      disableClickOutside
    >
      <Dialog.Header>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-primary">Save filter set</h2>
          <Button size="sm" variant="plain" onClick={handleClose}>
            <Icon icon={faTimes} />
          </Button>
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div className="flex items-center gap-2 p-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Availability</label>
            <Select
              data-testid="save-filter-set-type"
              size="lg"
              value={filterType}
              onChange={(e, value) => setFilterType(value as string)}
              className="rounded-full"
            >
              <Option value="personal">Personal</Option>
              <Option value="company">Company</Option>
            </Select>
          </div>
          <div className="flex flex-col gap-2 w-full">
            <label className="text-sm font-medium">Filter Name</label>
            <Input
              data-testid="save-filter-set-name"
              size="lg"
              placeholder="Enter filter name"
              autoFocus
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="rounded-full"
            />
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex gap-2 items-center justify-between w-full">
          <Button
            data-testid="save-filter-set-cancel"
            size="lg"
            variant="outlined"
            color="neutral"
            onClick={handleClose}
            className="w-full rounded-full"
          >
            Cancel
          </Button>
          <Button
            data-testid="save-filter-set-save"
            size="lg"
            variant="solid"
            color="primary"
            loading={isLoading}
            onClick={onSubmit}
            disabled={!filterName?.length}
            className="w-full rounded-full"
          >
            Save
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default SaveFilterModal;
